import * as React from 'react';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import XRegExp from 'xregexp';
import { Field } from 'react-final-form';
import { Form } from 'react-final-form';
import { FormApi } from 'final-form';
import { GridContainer } from '@pec/aion-ui-components/components/GridContainer';
import { isSingleAlphaCaps, isValidPhoneNumberWithMaxLength, maxLength } from '@pec/aion-ui-core/validators';
import { ITrainee } from '@pec/aion-ui-core/interfaces/trainee';
import { Link } from 'react-router-dom';
import { LoadingButton } from '@pec/aion-ui-components/components/LoadingButton';
import { Location } from 'history';
import { Paper } from '@pec/aion-ui-components/components/Paper';
import { parse } from '@pec/aion-ui-core/helpers/querystring';
import { TextField } from '@pec/aion-ui-deprecated/components/TextField';
import { useTranslation } from 'react-i18next';

type Props = {
  trainee: ITrainee;
  initialValues: ITrainee;
  onSubmit: (values: ITrainee, form: FormApi<ITrainee>) => void;
  location: Location;
};

export const EditContactInfoForm: React.FC<Props> = ({ initialValues, onSubmit, trainee, location }) => {
  const { t } = useTranslation();
  const userReferredByCbt = parse(location.search).from === 'eLearning/Student/';

  const strings = {
    cancel: t('user:profile.cancel', 'Cancel'),
    containsOnlyAlpha: t('user:profile.containsOnlyAlpha', 'must contain only alphabetic characters'),
    emergencyContactInformation: t('user:profile.emergencyContactInformation', 'Emergency Contact Information'),
    emergencyContactInstructions: t(
      'user:profile.emergencyContactInstructions',
      'Please provide the name and phone number of the person you would like to call in case of an emergency.'
    ),
    emergencyContactName: t('user:profile.emergencyContactName', 'Emergency Contact Name'),
    emergencyContactPhoneNumber: t('user:profile.emergencyContactPhoneNumber', 'Emergency Contact Phone'),
    emergencyContactRelation: t('user:profile.emergencyContactRelation', 'Emergency Contact Relation'),
    middleInitial: t('user:profile.middleInitial', 'Middle Initial'),
    phoneNumber: t('user:profile.phoneNumber', 'Personal Phone'),
    save: t('user:profile.save', 'Save'),
    updateContactInformation: t('user:profile.updateContactInformation', 'Update Contact Information')
  };

  // validate that string contains only unicode alpha characters (including non-English characters)
  const containsOnlyAlpha = (value: string) =>
    XRegExp('^[\\p{L}]+$').test(value) ? undefined : strings.containsOnlyAlpha;

  const composeValidators = (...validators: any[]) => (value: any) =>
    validators.reduce((error, validator) => error || validator(value), undefined);

  return (
    <GridContainer alignContent="center" justify="center">
      <Grid item xs={12}>
        <Typography variant="h4" align="center" gutterBottom>
          {trainee.firstName} {trainee.lastName}
        </Typography>
        <Typography variant="h6" align="center" gutterBottom>
          {strings.updateContactInformation}
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <Form initialValues={initialValues} onSubmit={onSubmit}>
          {({ handleSubmit, submitting, invalid, pristine }) => (
            <form onSubmit={handleSubmit}>
              <Paper>
                <GridContainer alignContent="center" justify="center">
                  {userReferredByCbt && (
                    <Grid item xs={12}>
                      <Field<string>
                        fullWidth
                        name="middleInitial"
                        label={strings.middleInitial}
                        component={TextField}
                        required
                        validate={isSingleAlphaCaps}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Field<string>
                      fullWidth
                      name="phoneNumber"
                      label={strings.phoneNumber}
                      component={TextField}
                      required={userReferredByCbt}
                      validate={maxLength(25)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1">{strings.emergencyContactInformation}</Typography>
                    <Typography variant="body2" gutterBottom>
                      {strings.emergencyContactInstructions}
                    </Typography>
                    <Field<string>
                      fullWidth
                      name="emergencyContactName"
                      label={strings.emergencyContactName}
                      component={TextField}
                      required={userReferredByCbt}
                      validate={maxLength(255)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field<string>
                      fullWidth
                      name="emergencyContactPhoneNumber"
                      label={strings.emergencyContactPhoneNumber}
                      component={TextField}
                      required={userReferredByCbt}
                      validate={isValidPhoneNumberWithMaxLength(25)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field<string>
                      fullWidth
                      name="emergencyContactRelation"
                      label={strings.emergencyContactRelation}
                      component={TextField}
                      required={userReferredByCbt}
                      validate={composeValidators(containsOnlyAlpha, maxLength(255))}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <GridContainer alignContent="center" justify="center">
                      {!userReferredByCbt && (
                        <Grid item>
                          <Button variant="text" color="primary" component={Link} to="/contact-info" fullWidth>
                            {strings.cancel}
                          </Button>
                        </Grid>
                      )}
                      <Grid item>
                        <LoadingButton
                          type="submit"
                          fullWidth
                          variant="contained"
                          color="primary"
                          disabled={submitting || invalid || pristine}
                          isSubmitting={submitting}
                        >
                          {submitting ? <CircularProgress size={24} color="inherit" /> : strings.save}
                        </LoadingButton>
                      </Grid>
                    </GridContainer>
                  </Grid>
                </GridContainer>
              </Paper>
            </form>
          )}
        </Form>
      </Grid>
    </GridContainer>
  );
};
